import {i18n} from '@lingui/core';
import {t, Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {EnumId, EnumParameter as EnumParameterModel} from '../../../editors/commons/parameterModel';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {RuleDefaultCardTypesEnum} from '../../ruleActionConditionHelpers';
import {buildRuleCondition} from '../../ruleConditionHelpers';
import {
  AnyRecipientCondition,
  BccCondition,
  CcCondition,
  FromCondition,
  RuleCondition,
  RuleConditionTypesEnum,
  ToCcCondition,
  ToCondition,
} from '../../ruleConditionModel';
import {RuleConditionDefinition} from '../../ruleParameterModel';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {EnumParameter} from '../parameters/enumParameter';
import {RuleConditionIcon} from '../ruleConditionIcon';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import {renderGenericRuleParameters} from './ruleDefaultActionConditionCard';

/*
 * Types.
 */

const recipientConditions: ReadonlyArray<RuleConditionTypesEnum> = [
  RuleConditionTypesEnum.MESSAGE_FROM,
  RuleConditionTypesEnum.MESSAGE_TO,
  RuleConditionTypesEnum.MESSAGE_CC,
  RuleConditionTypesEnum.MESSAGE_TO_CC,
  RuleConditionTypesEnum.MESSAGE_BCC,
  RuleConditionTypesEnum.MESSAGE_ANY_RECIPIENT,
];

const recipientEnumTitles = [
  i18n._(t('rule-condition-recipient-from')`From`),
  i18n._(t('rule-condition-recipient-to')`To`),
  i18n._(t('rule-condition-recipient-cc')`Cc`),
  i18n._(t('rule-condition-recipient-to-cc')`To/Cc`),
  i18n._(t('rule-condition-recipient-bcc')`Bcc`),
  i18n._(t('rule-condition-recipient-any')`Any recipient field`),
];

type RecipientCondition =
  | AnyRecipientCondition
  | FromCondition
  | ToCondition
  | CcCondition
  | ToCcCondition
  | BccCondition;

interface RecipientFieldConditionCardProps {
  condition: RecipientCondition;
  conditionDefinition: RuleConditionDefinition | undefined;
  errorMessage?: ReactNode;
  isDisabled?: boolean;
  onUpdate: HandlerOf<RuleCondition>;
  onDelete?: HandlerOf<RuleCondition>;
}

/*
 * Styles.
 */

const MiddleConditionLabel = styled.span`
  margin: 0 5px;
`;

/*
 * Constants.
 */

const recipientConditionEnumId = 'client-recipient-condition';

/*
 * Component.
 */

export const RuleRecipientFieldConditionCard: FC<RecipientFieldConditionCardProps> = (props) => {
  const {condition, conditionDefinition, errorMessage, isDisabled, onUpdate, onDelete} = props;

  // This should not happen, but safety check.
  const errorLabel = <Trans id="definition-condition-not-found">Condition {condition.type} not found</Trans>;
  if (!conditionDefinition) {
    return <RuleMultilineParameterCard icon={<RuleConditionIcon id={condition.type} />} title={errorLabel} />;
  }

  const recipientConditionEnum: EnumParameterModel = {
    id: recipientConditionEnumId,
    title: 'Recipient field',
    public: false,
    enum: recipientConditions,
    enumTitles: recipientEnumTitles,
    enumDescriptions: recipientEnumTitles,
  };

  const onRecipientConditionChange = (newConditionType: OrNull<SelectedDropdownItem<{id: EnumId}>>) => {
    // Expressions not supperted yet
    if (!newConditionType || isExpressionRef(newConditionType)) {
      return;
    }

    const newRuleCondition = buildRuleCondition(newConditionType as RuleCondition['type'], condition.params);
    onUpdate(newRuleCondition);
  };

  const onCancel = onDelete && (() => onDelete(condition));

  // Generic rendering is used for the leftover parameters.
  const {
    parameters: [stringCompareParam, ...otherParams],
  } = renderGenericRuleParameters(
    condition,
    conditionDefinition,
    RuleDefaultCardTypesEnum.CONDITION,
    onUpdate,
    conditionDefinition.parameters,
    isDisabled,
  );

  const title = (
    <>
      <EnumParameter
        enumParameter={recipientConditionEnum}
        selectedItem={condition.type}
        onChange={onRecipientConditionChange}
        isDisabled={props.isDisabled}
      />
      <MiddleConditionLabel>
        <Trans id="rule-recipeint-condition-card-field">field</Trans>
      </MiddleConditionLabel>
      {stringCompareParam}
    </>
  );

  return (
    <RuleMultilineParameterCard
      icon={<RuleConditionIcon id={condition.type} />}
      title={title}
      errorMessage={errorMessage}
      onCancel={onCancel}
    >
      {otherParams}
    </RuleMultilineParameterCard>
  );
};
